import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, errorMsg, modeloIdDe, colones } from "@/lib/api";
import { aplicarLimitePorTrabajo } from "@/lib/maximos";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Panel } from "@/components/Panel";
import { EliminarConConfirmacion } from "@/components/EliminarConConfirmacion";
import { FrtDialog } from "@/components/FrtDialog";
import { FrtLoteDialog } from "@/components/FrtLoteDialog";
import { PlantillaRepuestosDialog } from "@/components/PlantillaRepuestosDialog";
import { AvisoSinGrupoTarifa } from "@/components/AvisoSinGrupoTarifa";
import { logError } from "@/lib/logger";
import { ArrowLeft, Search, ChevronDown, ChevronRight, CornerDownRight,
  Timer, ListChecks, AlertTriangle, Copy as CopyIcon, Printer, Sparkles,
} from "lucide-react";
import { CotizacionImpresion } from "@/components/cotizacion/CotizacionImpresion";
import { SugerenciasIADialog } from "@/components/SugerenciasIADialog";

const fmtColones = (n) => `₡ ${Number(n || 0).toLocaleString("es-CR")}`;
const numCot = (n) => `COT-${String(n || 0).padStart(4, "0")}`;

export default function CotizacionTrabajo() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [trabajo, setTrabajo] = useState(null);
  const [notas, setNotas] = useState("");
  const [q, setQ] = useState("");
  const [resultados, setResultados] = useState([]);
  const [abierto, setAbierto] = useState(false);
  const [plantillaModal, setPlantillaModal] = useState(null);
  const [frtOpen, setFrtOpen] = useState(false);
  const [frtPedido, setFrtPedido] = useState("");
  const [loteOpen, setLoteOpen] = useState(false);
  const [loteData, setLoteData] = useState(null);
  const [loteCargando, setLoteCargando] = useState(false);
  const [colapsados, setColapsados] = useState({});
  const [colores, setColores] = useState([]);
  const [iaOpen, setIaOpen] = useState(false);
  const iaPendientesRef = useRef([]);
  const boxRef = useRef(null);

  const cargar = async () => {
    try {
      const { data } = await api.get(`/trabajos/${id}`);
      setTrabajo(data); setNotas(data.notas || "");
    } catch (e) { toast.error(errorMsg(e)); navigate("/cotizaciones"); }
  };
  useEffect(() => { cargar(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [id]);

  const modeloId = modeloIdDe(trabajo);

  useEffect(() => {
    if (!modeloId) { setColores([]); return; }
    api.get(`/modelos/${modeloId}/colores`).then((r) => setColores(r.data)).catch(() => setColores([]));
  }, [modeloId]);

  useEffect(() => {
    const fn = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setAbierto(false); };
    document.addEventListener("mousedown", fn);
    return () => document.removeEventListener("mousedown", fn);
  }, []);

  useEffect(() => {
    const term = q.trim();
    if (term.length < 2 || !modeloId) { setResultados([]); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/servicios-buscar", { params: { q: term, limit: 25, modelo_id: modeloId } });
        setResultados(data);
      } catch { setResultados([]); }
    }, 250);
    return () => clearTimeout(t);
  }, [q, modeloId]);

  const mapActual = ({ id: sid, tipo, ref_id, codigo, nombre, categoria, cantidad, prioridad, origen, plantilla_id, mano_obra, estado, aportado_cliente, ya_incluido, opcional, cantidad_maxima, maximo_excedido, incluido_en_kit, cobrar_igual, cubre_ref_ids }) =>
    ({ id: sid, tipo, ref_id, codigo, nombre, categoria, cantidad, prioridad, origen, plantilla_id, mano_obra, estado, aportado_cliente, ya_incluido, opcional, cantidad_maxima, maximo_excedido, incluido_en_kit, cobrar_igual, cubre_ref_ids });

  const guardar = async (solicitudes, eliminar_ids = []) => {
    try {
      const { data } = await api.put(`/trabajos/${id}/solicitudes`, { solicitudes, eliminar_ids });
      setTrabajo(data);
      return data;
    } catch (e) { toast.error(errorMsg(e)); throw e; }
  };

  const guardarNotas = async () => {
    if (notas === (trabajo?.notas || "")) return;
    try { await api.patch(`/trabajos/${id}`, { notas }); } catch (e) { toast.error(errorMsg(e)); }
  };

  const cambiarColor = async (valor) => {
    const codigo_color = valor === "sin" ? "" : valor;
    try {
      const { data } = await api.patch(`/trabajos/${id}`, { codigo_color });
      setTrabajo(data);
    } catch (e) { toast.error(errorMsg(e)); }
  };

  const yaExiste = (tipo, refId) => (trabajo?.solicitudes || []).some((s) => s.tipo === tipo && s.ref_id === refId);

  const agregarRepuestos = async (items) => {
    // items: [{ ref_id, codigo, nombre, categoria, cantidad }] — una sola lectura de 'trabajo'
    // y un solo guardado, para no perder líneas al agregar varias a la vez (ej. desde la IA).
    setAbierto(false); setQ("");
    const actuales = trabajo?.solicitudes || [];
    const yaEnCotizacion = new Set(actuales.filter((s) => s.tipo === "repuesto").map((s) => s.ref_id));
    const vistosLote = new Set();
    let duplicados = 0;
    const porAgregar = [];
    for (const it of items) {
      if (it.ref_id && (yaEnCotizacion.has(it.ref_id) || vistosLote.has(it.ref_id))) { duplicados++; continue; }
      if (it.ref_id) vistosLote.add(it.ref_id);
      porAgregar.push(it);
    }
    if (duplicados > 0) {
      toast.info(`${duplicados} repuesto${duplicados !== 1 ? "s" : ""} ya estaba${duplicados !== 1 ? "n" : ""} en la cotización y no se ${duplicados !== 1 ? "duplicaron" : "duplicó"}`);
    }
    if (porAgregar.length === 0) return;
    const existentesFijas = actuales.filter((s) => s.tipo === "repuesto").map((s) => ({ ...s, _fijarDecision: true }));
    const nuevasLineas = porAgregar.map((it) => ({ ref_id: it.ref_id, nombre: it.nombre,
      cantidad: it.cantidad || 1, cantidad_maxima: null, ya_incluido: false }));
    const ridsLote = [...new Set([...existentesFijas, ...nuevasLineas].map((l) => l.ref_id).filter(Boolean))];
    const componentesPorKit = ridsLote.length
      ? (await api.post("/repuestos/componentes", { ids: ridsLote }).catch(() => ({ data: {} }))).data : {};
    const decididas = aplicarLimitePorTrabajo([...existentesFijas, ...nuevasLineas], componentesPorKit).slice(existentesFijas.length);
    const nuevos = porAgregar.map((it, idx) => ({ id: null, tipo: "repuesto", ref_id: it.ref_id, codigo: it.codigo || "",
      nombre: it.nombre, categoria: it.categoria || "", cantidad: it.cantidad || 1, prioridad: "media",
      estado: "pendiente", origen: "manual", plantilla_id: null, mano_obra: null, aportado_cliente: false,
      ya_incluido: decididas[idx].ya_incluido, maximo_excedido: decididas[idx].maximo_excedido ?? null,
      incluido_en_kit: decididas[idx].incluido_en_kit ?? null }));
    await guardar([...actuales.map(mapActual), ...nuevos]);
  };

  const agregarRepuestoSuelto = (item) => agregarRepuestos([{ ...item, cantidad: 1 }]);

  const onRepuestoListoIA = (rep, cantidad) => {
    iaPendientesRef.current.push({ ref_id: rep.id, codigo: rep.codigo_parte || "", nombre: rep.nombre,
      categoria: rep.categoria || "", cantidad: cantidad || 1 });
  };

  const cerrarDialogoIA = async (open) => {
    setIaOpen(open);
    if (!open && iaPendientesRef.current.length) {
      const pendientes = iaPendientesRef.current;
      iaPendientesRef.current = [];
      await agregarRepuestos(pendientes);
    }
  };

  const agregarServicio = async (item) => {
    setAbierto(false); setQ("");
    if (yaExiste("servicio", item.ref_id)) { toast.info("Ese servicio ya está agregado"); return; }
    const actuales = trabajo?.solicitudes || [];
    let plantilla = null;
    try {
      const { data } = await api.get(`/servicios/${item.ref_id}/plantilla-resuelta`, { params: { modelo_id: modeloId } });
      plantilla = data.plantilla;
      if (data.aviso === "sin_plantilla_modelo") toast.info("No hay plantilla para este modelo; se agregó el servicio sin repuestos");
      if (data.aviso === "varias_plantillas") toast.info("Hay varias plantillas para este modelo; se usó la primera. Revise.");
    } catch { /* servicio sin plantilla */ }
    const items = plantilla?.items || [];
    const nuevoServicio = { id: null, tipo: "servicio", ref_id: item.ref_id, codigo: item.codigo || "", nombre: item.nombre,
      categoria: "", cantidad: 1, prioridad: "media", estado: "pendiente", origen: "manual",
      plantilla_id: plantilla?.id || null, mano_obra: plantilla ? Number(plantilla.mano_de_obra || 0) : null,
      aportado_cliente: false, ya_incluido: false };
    if (!items.length) { await guardar([...actuales.map(mapActual), nuevoServicio]); return; }
    const existentesFijas = actuales.filter((s) => s.tipo === "repuesto").map((s) => ({ ...s, _fijarDecision: true }));
    const nuevasLineas = items.map((it) => ({ ref_id: it.repuesto_id, nombre: it.nombre, cantidad: it.cantidad || 1,
      cantidad_maxima: it.cantidad_maxima ?? null, ya_incluido: false }));
    const ridsLote = [...new Set([...existentesFijas, ...nuevasLineas].map((l) => l.ref_id).filter(Boolean))];
    const componentesPorKit = ridsLote.length
      ? (await api.post("/repuestos/componentes", { ids: ridsLote }).catch(() => ({ data: {} }))).data : {};
    const decididas = aplicarLimitePorTrabajo([...existentesFijas, ...nuevasLineas], componentesPorKit).slice(existentesFijas.length);
    const repuestos = items.map((it, idx) => ({ id: null, tipo: "repuesto", ref_id: it.repuesto_id, codigo: it.codigo,
      nombre: it.nombre, categoria: it.categoria, cantidad: it.cantidad || 1, prioridad: "media",
      estado: "pendiente", origen: `plantilla:${item.ref_id}`, plantilla_id: plantilla.id, mano_obra: null,
      aportado_cliente: false, ya_incluido: decididas[idx].ya_incluido, opcional: !!it.opcional,
      cantidad_maxima: it.cantidad_maxima ?? null, maximo_excedido: decididas[idx].maximo_excedido ?? null,
      incluido_en_kit: decididas[idx].incluido_en_kit ?? null }));
    await guardar([...actuales.map(mapActual), nuevoServicio, ...repuestos]);
    setPlantillaModal({ servicioId: item.ref_id, servicioNombre: item.nombre,
      items: items.map((it) => ({ ref_id: it.repuesto_id, codigo: it.codigo, nombre: it.nombre,
        categoria: it.categoria, cantidad: it.cantidad || 1, aportado_cliente: false,
        opcional: !!it.opcional, cantidad_maxima: it.cantidad_maxima ?? null })) });
  };

  const agregar = (item) => (item.tipo === "servicio" ? agregarServicio(item) : agregarRepuestoSuelto(item));

  const confirmarPlantilla = async (reps) => {
    const sid = plantillaModal.servicioId;
    const actuales = trabajo?.solicitudes || [];
    const actualizadas = actuales.map((s) => {
      if (s.tipo === "repuesto" && s.origen === `plantilla:${sid}`) {
        const r = reps.find((x) => x.ref_id === s.ref_id);
        if (r) return { ...s, aportado_cliente: !!r.aportado_cliente };
      }
      return s;
    });
    await guardar(actualizadas.map(mapActual));
    setPlantillaModal(null);
  };

  const manoObraServicio = (sol) => Number(
    sol.mano_obra_efectiva !== undefined && sol.mano_obra_efectiva !== null
      ? sol.mano_obra_efectiva : (sol.mano_obra || 0));

  const cobrarIgual = async (sol, valor) => {
    try {
      const { data } = await api.post(`/trabajos/${id}/solicitudes/${sol.id}/cobrar-igual`, { valor });
      setTrabajo(data);
    } catch (e) { toast.error(errorMsg(e)); }
  };

  const abrirFrt = (pedido) => { setFrtPedido(pedido || ""); setFrtOpen(true); };

  const cubiertosPorFrt = useMemo(() => new Set(
    (trabajo?.solicitudes || [])
      .filter((s) => s.origen === "frt" && s.estado !== "rechazado")
      .map((s) => ((s.ref_id || "").split(":")[2] || "").trim())
      .filter(Boolean)), [trabajo]);

  // Repuestos cubiertos por una línea de conjunto "AMBOS" (ver agregarDesdeEstimador): se
  // guardan por ref_id en cubre_ref_ids, no por nombre, porque cubren 2 repuestos a la vez.
  const cubiertosPorConjunto = useMemo(() => new Set(
    (trabajo?.solicitudes || [])
      .filter((s) => s.estado !== "rechazado" && Array.isArray(s.cubre_ref_ids))
      .flatMap((s) => s.cubre_ref_ids)
      .filter(Boolean)), [trabajo]);

  const servicioSugeridoPorRef = useMemo(() => {
    const m = {};
    (trabajo?.solicitudes || [])
      .filter((s) => s.tipo === "servicio" && (s.origen || "").startsWith("estimado:") && s.estado !== "rechazado")
      .forEach((s) => {
        const refId = s.origen.slice("estimado:".length);
        if (!m[refId]) m[refId] = s.nombre;
      });
    return m;
  }, [trabajo]);

  const pendientesSinMO = () => {
    const sols = trabajo?.solicitudes || [];
    const serviciosPorRef = {};
    sols.forEach((s) => { if (s.tipo === "servicio") serviciosPorRef[s.ref_id] = s; });
    const tieneRepuestosDePlantilla = (sid) => sols.some((x) => x.origen === `plantilla:${sid}`);
    const filtradas = sols.filter((s) => {
      if (s.estado === "rechazado" || s.origen === "frt" || cubiertosPorFrt.has(s.nombre) || cubiertosPorConjunto.has(s.ref_id)
        || (s.tipo === "repuesto" && servicioSugeridoPorRef[s.ref_id])) return false;
      const m = (s.origen || "").match(/^plantilla:(.+)$/);
      if (m) {
        const padre = serviciosPorRef[m[1]];
        return !!padre && !padre.incluida_en && (!padre.plantilla_id || manoObraServicio(padre) === 0);
      }
      if (Number(s.mano_obra || 0)) return false;
      if (s.tipo === "servicio" && (!s.plantilla_id || manoObraServicio(s) === 0) && tieneRepuestosDePlantilla(s.ref_id)) {
        return false;
      }
      return true;
    });
    const vistos = new Set();
    return filtradas.filter((s) => {
      if (vistos.has(s.ref_id)) return false;
      vistos.add(s.ref_id);
      return true;
    });
  };

  const estimarLote = async () => {
    const items = pendientesSinMO().map((s) => ({
      nombre: s.nombre, codigo: s.codigo || "", repuesto_id: s.tipo === "repuesto" ? s.ref_id : null }));
    if (!items.length) { toast.info("No hay ítems marcados 'Sin mano de obra'"); return; }
    setLoteCargando(true);
    try {
      const { data } = await api.post("/frt/estimar-lote",
        { modelo_id: modeloId, items, trabajo_id: id }, { timeout: 180000 });
      if (data.sin_grupo) { toast.warning(data.mensaje); return; }
      setLoteData(data);
      setLoteOpen(true);
    } catch (e) { toast.error(errorMsg(e)); }
    finally { setLoteCargando(false); }
  };

  const agregarDesdeEstimador = async ({ servicios = [], frt = [], conjuntos = [] }) => {
    const actuales = trabajo?.solicitudes || [];
    const nuevosServicios = (servicios || [])
      .map((sg) => ({ id: null, tipo: "servicio", ref_id: sg.servicio_id, codigo: "",
        nombre: sg.servicio_nombre, categoria: "", cantidad: 1, prioridad: "media",
        origen: sg.repuesto_ref_id ? `estimado:${sg.repuesto_ref_id}` : "manual",
        plantilla_id: sg.plantilla_id, mano_obra: sg.mano_obra }))
      .filter((n) => !actuales.some((s) => s.ref_id === n.ref_id));
    const nuevosFrt = (frt || [])
      .map((c) => ({ id: null, tipo: "servicio",
        ref_id: `frt:${c.id}${c.repuesto ? `:${c.repuesto}` : ""}`, codigo: "FRT",
        nombre: `${c.operacion}${c.alcance ? ` (${c.alcance})` : ""} — ESTIMADO FRT ${c.frt_horas}h${c.repuesto ? ` · MO de: ${c.repuesto}` : ""}`,
        categoria: "", cantidad: 1, prioridad: "media", origen: "frt",
        plantilla_id: null, mano_obra: c.total }))
      .filter((n) => !actuales.some((s) => s.ref_id === n.ref_id));
    const nuevosConjuntos = (conjuntos || [])
      .map((g) => ({ id: null, tipo: "servicio",
        ref_id: `frt:${g.id}:${g.repuesto1.nombre}+${g.repuesto2.nombre}`, codigo: "FRT",
        nombre: `${g.operacion} ${g.alcance} — ESTIMADO FRT ${g.frt_horas}h · MO de: ${g.repuesto1.nombre} + ${g.repuesto2.nombre}`,
        categoria: "", cantidad: 1, prioridad: "media",
        origen: g.repuesto1.ref_id ? `estimado:${g.repuesto1.ref_id}` : "frt",
        cubre_ref_ids: [g.repuesto1.ref_id, g.repuesto2.ref_id].filter(Boolean),
        plantilla_id: null, mano_obra: g.total }))
      .filter((n) => !actuales.some((s) => s.ref_id === n.ref_id));
    const nuevos = [...nuevosServicios, ...nuevosFrt, ...nuevosConjuntos];
    if (!nuevos.length) return;
    await guardar([...actuales.map(mapActual), ...nuevos]);
  };

  const agregarEstimadosFrt = async (items) => {
    const actuales = trabajo?.solicitudes || [];
    const nuevos = items
      .map((c) => ({ id: null, tipo: "servicio",
        ref_id: `frt:${c.id}${c.repuesto ? `:${c.repuesto}` : ""}`, codigo: "FRT",
        nombre: `${c.operacion}${c.alcance ? ` (${c.alcance})` : ""} — ESTIMADO FRT ${c.frt_horas}h${c.repuesto ? ` · MO de: ${c.repuesto}` : ""}`,
        categoria: "", cantidad: 1, prioridad: "media", origen: "frt",
        plantilla_id: null, mano_obra: c.total }))
      .filter((n) => !actuales.some((s) => s.ref_id === n.ref_id));
    if (!nuevos.length) return;
    await guardar([...actuales.map(mapActual), ...nuevos]);
  };

  const eliminarSolicitud = async (sol) => {
    const actuales = trabajo?.solicitudes || [];
    let restantes = actuales.filter((s) => s.id !== sol.id);
    const eliminarIds = [sol.id];
    if (sol.tipo === "servicio") {
      const hijos = actuales.filter((s) => s.origen === `plantilla:${sol.ref_id}`);
      restantes = restantes.filter((s) => s.origen !== `plantilla:${sol.ref_id}`);
      eliminarIds.push(...hijos.map((h) => h.id));
    }
    await guardar(restantes.map(mapActual), eliminarIds);
  };

  const solicitudes = useMemo(() => trabajo?.solicitudes || [], [trabajo]);

  const filasAgrupadas = useMemo(() => {
    const presentes = new Set(solicitudes.filter((s) => s.tipo === "servicio").map((s) => s.ref_id));
    const hijosDe = {};
    solicitudes.forEach((s, i) => {
      const m = (s.origen || "").match(/^plantilla:(.+)$/);
      if (m && presentes.has(m[1])) (hijosDe[m[1]] = hijosDe[m[1]] || []).push({ s, i });
    });
    const enGrupo = new Set(Object.values(hijosDe).flat().map((x) => x.i));
    const frt = [];
    solicitudes.forEach((s, i) => { if (s.origen === "frt" || (s.origen || "").startsWith("estimado:")) frt.push({ s, i }); });
    const enFrt = new Set(frt.map((x) => x.i));
    const filas = [];
    solicitudes.forEach((s, i) => {
      if (enGrupo.has(i) || enFrt.has(i)) return;
      const hijos = s.tipo === "servicio" ? hijosDe[s.ref_id] || [] : [];
      if (hijos.length) {
        filas.push({ s, i, rol: "padre", nHijos: hijos.length });
        if (!colapsados[s.ref_id]) hijos.forEach((h) => filas.push({ ...h, rol: "hijo" }));
      } else {
        filas.push({ s, i, rol: "suelto" });
      }
    });
    if (frt.length) {
      const total = frt.reduce((a, x) => a + (x.s.estado !== "rechazado" ? Number(x.s.mano_obra || 0) : 0), 0);
      filas.push({ rol: "frt-padre", i: "frt-padre", nHijos: frt.length, total });
      if (!colapsados.__frt__) frt.forEach((x) => filas.push({ ...x, rol: "frt-hijo" }));
    }
    return filas;
  }, [solicitudes, colapsados]);

  const gruposRender = useMemo(() => {
    const grupos = [];
    let actual = null;
    filasAgrupadas.forEach((f) => {
      if (f.rol === "padre" || f.rol === "frt-padre") {
        actual = { filas: [f] };
        grupos.push(actual);
      } else if ((f.rol === "hijo" || f.rol === "frt-hijo") && actual) {
        actual.filas.push(f);
      } else {
        grupos.push({ filas: [f] });
        actual = null;
      }
    });
    grupos.forEach((g) => {
      const ultimo = g.filas[g.filas.length - 1];
      if (ultimo.rol === "hijo" || ultimo.rol === "frt-hijo") {
        g.filas[g.filas.length - 1] = { ...ultimo, esUltimoHijo: true };
      }
    });
    return grupos;
  }, [filasAgrupadas]);

  const parseFrt = (s) => {
    const m = (s.nombre || "").match(/^(.*?) — ESTIMADO FRT ([\d.]+)h(?: · MO de: (.*))?$/);
    return m ? { operacion: m[1], horas: m[2], repuesto: m[3] || "" }
      : { operacion: s.nombre, horas: "", repuesto: "" };
  };

  const totalMO = solicitudes
    .filter((s) => s.tipo === "servicio")
    .reduce((acc, s) => acc + manoObraServicio(s), 0);

  const copiarLista = async () => {
    const lineas = [
      `${numCot(trabajo.numero)} · ${trabajo.modelo || ""}${trabajo.referencia ? ` · ${trabajo.referencia}` : ""}`, "",
      "MANO DE OBRA:",
      ...solicitudes.filter((s) => s.tipo === "servicio").map((s) => `${s.nombre}\t${colones(manoObraServicio(s))}`),
      `TOTAL MANO DE OBRA: ${colones(totalMO)}`,
    ];
    try {
      await navigator.clipboard.writeText(lineas.join("\n"));
      toast.success("Lista copiada al portapapeles");
    } catch (error) {
      logError("Error al copiar al portapapeles:", error);
      toast.error("No se pudo copiar al portapapeles");
    }
  };

  if (!trabajo) return <div className="text-sm text-slate-400">Cargando...</div>;

  return (
    <div data-testid="cotizacion-trabajo-page" className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3 no-print">
        <div className="flex items-center gap-2 min-w-0">
          <Button variant="ghost" size="icon" data-testid="cotizacion-volver-btn" onClick={() => navigate("/cotizaciones")}>
            <ArrowLeft size={17} />
          </Button>
          <h1 className="font-heading text-lg font-bold truncate" data-testid="cotizacion-titulo">
            Cotización {numCot(trabajo.numero)} · {trabajo.modelo}{trabajo.referencia ? ` · ${trabajo.referencia}` : ""}
            {trabajo.codigo_color && ` · Color ${trabajo.codigo_color}${trabajo.color_nombre ? ` ${trabajo.color_nombre}` : ""}`}
          </h1>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {colores.length > 0 && (
            <Select value={trabajo.codigo_color || "sin"} onValueChange={cambiarColor}>
              <SelectTrigger data-testid="cotizacion-color-editar" className="h-8 w-44 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="sin">Sin color</SelectItem>
                {colores.map((c) => (
                  <SelectItem key={c.codigo} value={c.codigo}>
                    {c.nombre ? `${c.codigo} · ${c.nombre}` : c.codigo}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Button data-testid="cotizacion-copiar-btn" variant="outline" size="sm" onClick={copiarLista}>
            <CopyIcon size={14} className="mr-1" /> Copiar lista
          </Button>
          <Button data-testid="cotizacion-imprimir-btn" variant="outline" size="sm" onClick={() => window.print()}>
            <Printer size={14} className="mr-1" /> Imprimir
          </Button>
        </div>
      </div>

      <Panel titulo="Repuestos y servicios de la cotización" testId="cotizacion-panel" className="no-print">
        <div className="flex flex-wrap items-center gap-2">
          <div ref={boxRef} className="relative flex-1 min-w-56">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input data-testid="cotizacion-buscar-input" value={q}
              onChange={(e) => { setQ(e.target.value); setAbierto(true); }}
              onFocus={() => setAbierto(true)}
              placeholder="Buscar repuesto o servicio..." className="pl-9" />
            {abierto && q.trim() && (
              <div data-testid="cotizacion-resultados"
                className="absolute z-20 mt-1 w-full rounded-md border border-slate-200 bg-white shadow-lg max-h-72 overflow-y-auto overscroll-contain">
                {resultados.map((r, i) => (
                  <button key={`${r.tipo}-${r.ref_id}`} type="button" data-testid={`cotizacion-resultado-${i}`}
                    onClick={() => agregar(r)} disabled={yaExiste(r.tipo, r.ref_id)}
                    className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm hover:bg-slate-50 disabled:opacity-40 border-b border-slate-50 last:border-0">
                    <span className={`shrink-0 rounded px-1.5 py-0.5 text-[10px] font-bold uppercase ${
                      r.tipo === "servicio" ? "bg-indigo-50 text-indigo-700" : "bg-teal-50 text-teal-700"}`}>
                      {r.tipo === "servicio" ? "Servicio" : "Repuesto"}
                    </span>
                    <span className="truncate text-slate-700">{r.nombre}</span>
                    {r.tipo === "repuesto" && <span className="ml-auto shrink-0 text-xs text-slate-400">{r.codigo} · {r.categoria || "—"}</span>}
                    {yaExiste(r.tipo, r.ref_id) && <span className="ml-auto shrink-0 text-xs text-slate-400">ya agregado</span>}
                  </button>
                ))}
                {resultados.length === 0 && (
                  <div className="px-3 py-2">
                    <p className="text-sm text-slate-400">Sin resultados para este modelo</p>
                    {modeloId && (
                      <button type="button" data-testid="cotizacion-pedir-ia-link"
                        onClick={() => { setAbierto(false); setIaOpen(true); }}
                        className="mt-1 inline-flex items-center gap-1.5 text-xs font-medium text-indigo-600 hover:text-indigo-800">
                        <Sparkles size={13} /> Pedir a la IA
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
          <Button type="button" variant="outline" size="sm" data-testid="cotizacion-pedir-ia-btn"
            disabled={!modeloId} onClick={() => setIaOpen(true)}
            className="border-indigo-200 text-indigo-700 hover:bg-indigo-50 hover:text-indigo-800">
            <Sparkles size={14} className="mr-1.5" /> Pedir a la IA
          </Button>
          <Button type="button" variant="outline" size="sm" data-testid="cotizacion-frt-lote-btn"
            disabled={!modeloId || loteCargando} onClick={estimarLote}
            className="border-teal-200 text-teal-700 hover:bg-teal-50 hover:text-teal-800">
            <ListChecks size={14} className="mr-1.5" />
            {loteCargando ? "Estimando pendientes..." : "Estimar mano de obra de los pendientes"}
          </Button>
          <Button type="button" variant="outline" size="sm" data-testid="cotizacion-frt-btn"
            disabled={!modeloId} onClick={() => abrirFrt("")}
            className="border-amber-200 text-amber-700 hover:bg-amber-50 hover:text-amber-800">
            <Timer size={14} className="mr-1.5" /> Estimar mano de obra por FRT
          </Button>
        </div>
        {trabajo?.sin_grupo_tarifa && <div className="mt-2"><AvisoSinGrupoTarifa testId="cotizacion-aviso-sin-grupo-tarifa" /></div>}

        <div className="mt-4 overflow-x-auto">
          <table data-testid="cotizacion-tabla" className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left text-xs uppercase text-slate-500">
                <th className="py-2.5 pr-3 pl-4 font-semibold">Repuesto / Servicio</th>
                <th className="py-2.5 pr-3 font-semibold">Cant.</th>
                <th className="py-2.5 pr-3 font-semibold text-right">Mano de obra</th>
                <th className="py-2.5 pr-4 text-right font-semibold">Acciones</th>
              </tr>
            </thead>
            {gruposRender.map((g, gi) => (
              <tbody key={gi}>
                {gi > 0 && (
                  <tr aria-hidden="true">
                    <td colSpan={4} style={{ height: 12, padding: 0, border: "none", background: "transparent" }} />
                  </tr>
                )}
                {g.filas.map(({ s, i, rol, nHijos, total, esUltimoHijo }) => {
                  if (rol === "frt-padre") {
                    return (
                      <tr key="frt-padre" data-testid="cotizacion-frt-padre" className="bg-[#B45309] text-white">
                        <td className="py-2.5 pr-3 pl-4">
                          <div className="flex items-center gap-2">
                            <button type="button" data-testid="cotizacion-frt-colapsar"
                              onClick={() => setColapsados((c) => ({ ...c, __frt__: !c.__frt__ }))}
                              className="w-5 h-5 flex items-center justify-center rounded text-white hover:bg-white/15">
                              {colapsados.__frt__ ? <ChevronRight size={15} /> : <ChevronDown size={15} />}
                            </button>
                            <span className="font-medium text-[15px] text-white">Mano de obra estimada</span>
                            <span data-testid="cotizacion-frt-count"
                              className="chip rounded-full bg-white text-[#B45309]">
                              {nHijos} {nHijos === 1 ? "estimación" : "estimaciones"}
                            </span>
                          </div>
                        </td>
                        <td className="py-2.5 pr-3 text-white/90">—</td>
                        <td className="py-2.5 pr-3 text-right font-semibold text-white" data-testid="cotizacion-frt-total">
                          {fmtColones(total)}
                        </td>
                        <td className="py-2.5 pr-4"></td>
                      </tr>
                    );
                  }
                  const frtInfo = rol === "frt-hijo" && s.origen === "frt" ? parseFrt(s) : null;
                  const claseFila = rol === "padre" ? "bg-[#216295] text-white"
                    : rol === "hijo" || rol === "frt-hijo" ? `bg-white border-x border-slate-300 ${esUltimoHijo ? "border-b border-slate-300" : "border-b border-slate-200"}`
                    : "border-b border-slate-100";
                  const codigoCat = [s.codigo, s.categoria].filter(Boolean).join(" · ");
                  return (
                    <tr key={s.id} data-testid={`cotizacion-row-${i}`} data-rol={rol} className={claseFila}>
                      <td className="py-2.5 pr-3 pl-4">
                        <div className={rol === "hijo" || rol === "frt-hijo" ? "ml-3 sm:ml-6" : ""}>
                          {rol === "frt-hijo" ? (
                            s.origen === "frt" ? (
                              <>
                                <div className="flex flex-wrap items-center gap-2">
                                  <span className="font-medium text-slate-800">{frtInfo.operacion}</span>
                                  {frtInfo.horas && <span className="text-xs text-slate-500">{frtInfo.horas}h</span>}
                                  <span className="chip rounded bg-amber-100 uppercase text-amber-700">Estimado por FRT</span>
                                </div>
                                {frtInfo.repuesto && (
                                  <div className="mt-0.5 text-xs">
                                    <span className="chip gap-1 rounded bg-amber-100 text-amber-800">
                                      <CornerDownRight size={11} /> de: {frtInfo.repuesto}
                                    </span>
                                  </div>
                                )}
                              </>
                            ) : (
                              <>
                                <div className="flex flex-wrap items-center gap-2">
                                  <span className="font-medium text-slate-800">{s.nombre}</span>
                                  <span className="chip rounded bg-amber-100 uppercase text-amber-700">Servicio sugerido</span>
                                </div>
                                <div className="mt-0.5 text-xs">
                                  <span className="chip gap-1 rounded bg-amber-100 text-amber-800">
                                    <CornerDownRight size={11} /> de: {solicitudes.find((x) => x.ref_id === s.origen.slice("estimado:".length))?.nombre || "repuesto"}
                                  </span>
                                </div>
                              </>
                            )
                          ) : (
                            <>
                              <div className="flex items-center gap-2">
                                {rol === "padre" && (
                                  <button type="button" data-testid={`cotizacion-colapsar-${i}`}
                                    onClick={() => setColapsados((c) => ({ ...c, [s.ref_id]: !c[s.ref_id] }))}
                                    className="w-5 h-5 flex items-center justify-center rounded text-white hover:bg-white/15">
                                    {colapsados[s.ref_id] ? <ChevronRight size={15} /> : <ChevronDown size={15} />}
                                  </button>
                                )}
                                <span className={`font-medium ${rol === "padre" ? "text-[14px] text-white" : "text-slate-800"}`}>{s.nombre}</span>
                                {rol === "padre" && (
                                  <span data-testid={`cotizacion-hijos-count-${i}`} className="chip rounded-full bg-white text-[#216295]">
                                    {nHijos} repuesto{nHijos > 1 ? "s" : ""}
                                  </span>
                                )}
                              </div>
                              {codigoCat && (
                                <div className={`text-xs ${rol === "padre" ? "ml-7 text-white/80" : "text-slate-500"}`}>{codigoCat}</div>
                              )}
                            </>
                          )}
                        </div>
                      </td>
                      <td className={`py-2.5 pr-3 ${rol === "padre" ? "text-white" : "text-slate-700"}`}>{s.tipo === "repuesto" ? s.cantidad : "—"}</td>
                      <td className="py-2.5 pr-3 text-right" data-testid={`cotizacion-mo-${i}`}>
                        {s.tipo === "servicio" && s.cubierta_por ? (
                          s.cobrar_igual ? (
                            <div className="flex flex-col items-end gap-0.5">
                              <span className={rol === "padre" ? "text-white" : "text-slate-700"}>{fmtColones(manoObraServicio(s))}</span>
                              <button type="button" onClick={() => cobrarIgual(s, false)}
                                className="text-[11px] font-medium text-slate-500 underline hover:text-teal-700">
                                Volver a incluir
                              </button>
                            </div>
                          ) : (
                            <div className="flex flex-col items-end gap-0.5">
                              <span className="chip rounded bg-teal-100 text-teal-800">Incluida en {s.cubierta_por}</span>
                              <button type="button" onClick={() => cobrarIgual(s, true)}
                                className="text-[11px] font-medium text-amber-600 underline hover:text-amber-700">
                                Cobrar de todos modos
                              </button>
                            </div>
                          )
                        ) : s.tipo === "servicio" && manoObraServicio(s) > 0 ? (
                          <span className={rol === "padre" ? "text-white" : "text-slate-700"}>{fmtColones(manoObraServicio(s))}</span>
                        ) : (cubiertosPorFrt.has(s.nombre) || cubiertosPorConjunto.has(s.ref_id)) ? (
                          <span className="text-xs font-medium text-teal-700">estimada por FRT ↓</span>
                        ) : s.tipo === "repuesto" && servicioSugeridoPorRef[s.ref_id] ? (
                          <span className="text-xs font-medium text-teal-700">en: {servicioSugeridoPorRef[s.ref_id]} ↓</span>
                        ) : (s.tipo === "servicio" || rol === "suelto") ? (
                          <button type="button" data-testid={`cotizacion-sin-mo-${i}`}
                            title="Sin mano de obra asignada — clic para estimar por FRT"
                            onClick={() => abrirFrt(s.nombre)}
                            className="chip gap-1 rounded-full border border-amber-300 bg-amber-50 text-amber-700 hover:bg-amber-100">
                            <AlertTriangle size={11} /> Sin mano de obra
                          </button>
                        ) : (
                          <span className={rol === "padre" ? "text-white" : "text-slate-700"}>—</span>
                        )}
                      </td>
                      <td className="py-2.5 pr-4">
                        <div className="flex justify-end">
                          <EliminarConConfirmacion
                            mensaje={s.tipo === "servicio" ? "¿Eliminar el servicio y sus repuestos?" : "¿Eliminar esta línea?"}
                            onConfirmar={() => eliminarSolicitud(s)}
                            testId={`cotizacion-eliminar-${i}`} />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            ))}
            {solicitudes.length === 0 && (
              <tbody>
                <tr><td colSpan={4} className="py-8 text-center text-slate-400" data-testid="cotizacion-vacio">
                  Use el buscador para agregar repuestos o servicios a la cotización
                </td></tr>
              </tbody>
            )}
          </table>
        </div>
        {solicitudes.some((s) => s.tipo === "servicio") && (
          <div className="mt-4 flex justify-end text-sm" data-testid="cotizacion-total">
            Total mano de obra: <b data-testid="cotizacion-total-mo" className="ml-1.5">{fmtColones(totalMO)}</b>
          </div>
        )}

        <div className="mt-6">
          <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Notas</label>
          <Input data-testid="cotizacion-notas-input" value={notas}
            onChange={(e) => setNotas(e.target.value)} onBlur={guardarNotas}
            placeholder="Notas de la cotización (opcional)" className="mt-1" />
        </div>
      </Panel>

      {modeloId && (
        <div className="no-print">
          <FrtDialog open={frtOpen} onOpenChange={setFrtOpen}
            modeloId={modeloId} modeloNombre={trabajo.modelo} pedidoInicial={frtPedido}
            onAgregar={agregarEstimadosFrt} />
          <SugerenciasIADialog open={iaOpen} onOpenChange={cerrarDialogoIA}
            modeloId={modeloId} modeloNombre={trabajo.modelo} pedidoInicial={q}
            codigoColor={trabajo.codigo_color} usarCantidad
            onRepuestoListo={onRepuestoListoIA} />
        </div>
      )}

      <div className="no-print">
        <FrtLoteDialog open={loteOpen} onOpenChange={setLoteOpen} data={loteData} onAgregar={agregarDesdeEstimador} />

        <PlantillaRepuestosDialog open={!!plantillaModal} onOpenChange={(o) => { if (!o) setPlantillaModal(null); }}
          servicioNombre={plantillaModal?.servicioNombre} items={plantillaModal?.items || []}
          onConfirmar={confirmarPlantilla} />
      </div>

      <CotizacionImpresion trabajo={trabajo} solicitudes={solicitudes} notas={notas} />
    </div>
  );
}
